import axios, { type InternalAxiosRequestConfig, type AxiosError } from 'axios';
import type {
  Organization,
  Plan,
  Subscription,
  Invoice,
  MagicLinkResponse,
} from '../types';
import type { ThemeMode } from '../types/theme';

// User Settings interface
export interface UserSettings {
  email: string;
  themeMode: ThemeMode;
  preferences?: Record<string, unknown>;
}

const API_BASE_URL =
  import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001';

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add request interceptor to include auth token
api.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = localStorage.getItem('customer_portal_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// API Functions
export const apiService = {
  // Unified login — ask the backend which auth method to prompt with.
  // Customers (and unknown emails) → magic_link; admins/partners → password_mfa.
  // Unknown emails return magic_link to prevent enumeration.
  checkAuthMethod: async (
    email: string,
  ): Promise<{ method: 'magic_link' | 'password_mfa'; email: string }> => {
    const response = await api.post('/api/auth/email-check', { email });
    if (!response.data?.success) {
      throw new Error(response.data?.error || 'Failed to check email');
    }
    return response.data.data as {
      method: 'magic_link' | 'password_mfa';
      email: string;
    };
  },

  // Magic link authentication - now uses real SendGrid integration
  sendMagicLink: async (email: string): Promise<MagicLinkResponse> => {
    try {
      if (import.meta.env.DEV) {
        console.log('🔍 Frontend: Sending magic link request for:', email);
      }
      const response = await api.post('/api/customer/send-magic-link', {
        email,
      });
      if (import.meta.env.DEV) {
        console.log('📨 Frontend: API response:', response.data);
      }

      if (response.data.success) {
        return {
          success: true,
          message: response.data.message,
        };
      } else {
        return {
          success: false,
          message:
            response.data.message ||
            response.data.error ||
            'Failed to send magic link',
        };
      }
    } catch (error: unknown) {
      console.error('Magic link error:', error);

      type ErrorResponse = { error?: string; message?: string };
      const axiosErr = error as AxiosError<ErrorResponse>;
      const respData = axiosErr.response?.data;
      if (respData && (respData.error || respData.message)) {
        return {
          success: false,
          message:
            respData.error || respData.message || 'Failed to send magic link',
        };
      }

      return {
        success: false,
        message: 'Error sending magic link. Please try again.',
      };
    }
  },

  // Validate 6-digit verification code
  validateCode: async (
    email: string,
    code: string,
  ): Promise<{
    success: boolean;
    email?: string;
    organizations?: Organization[];
    attemptsRemaining?: number;
    message?: string;
  }> => {
    try {
      const response = await api.post('/api/customer/validate-code', {
        email,
        code,
      });

      if (response.data.success) {
        return {
          success: true,
          email: response.data.email,
          organizations: response.data.organizations,
        };
      } else {
        return {
          success: false,
          message: response.data.message || 'Invalid code',
          attemptsRemaining: response.data.attemptsRemaining,
        };
      }
    } catch (error: unknown) {
      const axiosErr = error as AxiosError<{
        error?: string;
        attemptsRemaining?: number;
      }>;
      const status = axiosErr.response?.status;
      const data = axiosErr.response?.data;

      if (status === 429) {
        return {
          success: false,
          message: 'Too many attempts. Please request a new code.',
          attemptsRemaining: 0,
        };
      }

      return {
        success: false,
        message: data?.error || 'Invalid verification code',
        attemptsRemaining: data?.attemptsRemaining,
      };
    }
  },

  // Validate magic link token
  validateToken: async (
    token: string,
  ): Promise<{
    success: boolean;
    email?: string;
    organizations?: Organization[];
  }> => {
    try {
      const response = await api.post('/api/customer/validate-token', {
        token,
      });

      if (response.data.success) {
        return {
          success: true,
          email: response.data.email,
          organizations: response.data.organizations,
        };
      } else {
        return {
          success: false,
        };
      }
    } catch (error) {
      console.error('Token validation error:', error);
      return {
        success: false,
      };
    }
  },

  // Get organizations by email
  getOrganizationsByEmail: async (email: string): Promise<Organization[]> => {
    const response = await api.get(
      `/api/customer/organizations-by-email/${encodeURIComponent(email)}`,
    );
    return response.data.data || [];
  },

  // Get all available plans
  getPlans: async (): Promise<Plan[]> => {
    const response = await api.get('/api/pricing/plans');
    return response.data.data || [];
  },

  // Get organization details
  getOrganization: async (orgId: string): Promise<Organization> => {
    const response = await api.get(`/api/organisations/${orgId}`);
    return response.data.data;
  },

  // Get subscription for organization
  getSubscription: async (orgId: string): Promise<Subscription | null> => {
    try {
      const response = await api.get(
        `/api/billing/organizations/${orgId}/subscription`,
      );
      return response.data.data || null;
    } catch (error) {
      console.error('Error fetching subscription:', error);
      return null;
    }
  },

  // Get invoices for organization
  getInvoices: async (
    orgId: string,
    limit: number = 10,
  ): Promise<Invoice[]> => {
    try {
      const response = await api.get(
        `/api/billing/organizations/${orgId}/invoices?limit=${limit}`,
      );
      return response.data.data || [];
    } catch (error) {
      console.error('Error fetching invoices:', error);
      return [];
    }
  },

  // Cancel subscription - using customer portal token
  cancelSubscription: async (
    orgId: string,
    cancelAtPeriodEnd: boolean = true,
  ): Promise<boolean> => {
    try {
      // Get portal token first
      const token = await apiService.generatePortalToken(orgId);
      const response = await api.post(`/api/customer-portal/${token}/cancel`, {
        reason: cancelAtPeriodEnd
          ? 'Cancelled by customer at period end'
          : 'Cancelled by customer immediately',
        feedback: '',
      });
      return response.data.success;
    } catch (error) {
      console.error('Error canceling subscription:', error);
      return false;
    }
  },

  // Reactivate subscription
  reactivateSubscription: async (orgId: string): Promise<boolean> => {
    try {
      const response = await api.post(
        `/api/billing/organizations/${orgId}/subscription/reactivate`,
      );
      return response.data.success;
    } catch (error) {
      console.error('Error reactivating subscription:', error);
      return false;
    }
  },

  // Create checkout session for plan change
  createCheckoutSession: async (
    planId: string,
    orgId?: string,
  ): Promise<string | null> => {
    try {
      const response = await api.post(`/api/stripe/plans/${planId}/checkout`, {
        organizationId: orgId,
        successUrl: `${window.location.origin}/success`,
        cancelUrl: `${window.location.origin}/dashboard`,
      });
      return response.data.url || null;
    } catch (error) {
      console.error('Error creating checkout session:', error);
      return null;
    }
  },

  // Customer Portal API functions
  // Authenticate portal with token
  authenticatePortal: async (token: string) => {
    try {
      const response = await api.get(`/api/customer-portal/${token}`);
      return response.data;
    } catch (error) {
      console.error('Error authenticating portal:', error);
      throw error;
    }
  },

  // Activate plan with automated billing (requires payment method)
  activateAutomatedPlan: async (token: string) => {
    try {
      const response = await api.post(`/api/customer-portal/${token}/checkout`);
      return response.data;
    } catch (error) {
      console.error('Error activating automated plan:', error);
      throw error;
    }
  },

  // Activate plan with invoice billing (no payment method required)
  activateInvoicePlan: async (token: string) => {
    try {
      const response = await api.post(
        `/api/customer-portal/${token}/activate-invoice`,
      );
      return response.data;
    } catch (error) {
      console.error('Error activating invoice plan:', error);
      throw error;
    }
  },

  // Get portal status
  getPortalStatus: async (token: string) => {
    try {
      const response = await api.get(`/api/customer-portal/${token}/status`);
      return response.data;
    } catch (error) {
      console.error('Error getting portal status:', error);
      throw error;
    }
  },

  // Organization-specific functions
  // Get detailed organization info
  getOrganizationDetails: async (orgId: string): Promise<Organization> => {
    const response = await api.get(`/api/organisations/${orgId}`);
    return response.data.data;
  },

  // Generate customer portal token for organization
  generatePortalToken: async (orgId: string): Promise<string> => {
    const response = await api.post(
      `/api/organisations/${orgId}/generate-portal-token`,
    );
    return response.data.data.token;
  },

  // Assign plan to organization
  assignPlan: async (
    orgId: string,
    planId: string,
    billingMethod: 'automated' | 'invoice',
  ) => {
    try {
      const response = await api.post(
        `/api/organisations/${orgId}/assign-plan`,
        {
          planId,
          billingMethod,
        },
      );
      return response.data;
    } catch (error) {
      console.error('Error assigning plan:', error);
      throw error;
    }
  },

  // Activate organization plan
  activateOrganizationPlan: async (orgId: string) => {
    try {
      const response = await api.post(
        `/api/organisations/${orgId}/activate-plan`,
      );
      return response.data;
    } catch (error) {
      console.error('Error activating organization plan:', error);
      throw error;
    }
  },

  // Stripe Elements helpers
  // Get Stripe configuration
  getStripeConfig: async (): Promise<{ publishableKey: string }> => {
    const response = await api.get('/api/stripe/config');
    if (!response.data?.success)
      throw new Error('Stripe configuration not available');
    return { publishableKey: response.data.data.publishableKey };
  },

  // Create Elements subscription
  createElementsSubscription: async (payload: {
    planId: string;
    customerEmail?: string;
    organisationId?: string;
  }) => {
    const response = await api.post(
      '/api/stripe/elements/subscription',
      payload,
    );
    if (!response.data?.success)
      throw new Error(response.data?.error || 'Failed to create subscription');
    return response.data.data as {
      clientSecret: string;
      publishableKey?: string;
    };
  },

  // Create billing portal session
  createBillingPortalSession: async (
    orgId: string,
    returnUrl?: string,
  ): Promise<string | null> => {
    try {
      const response = await api.post(
        `/api/billing/organizations/${orgId}/billing-portal`,
        {
          returnUrl: returnUrl || `${window.location.origin}/dashboard`,
        },
      );
      return response.data.data?.url || null;
    } catch (error) {
      console.error('Error creating billing portal session:', error);
      return null;
    }
  },

  // User Settings API functions
  // Get user settings by email
  getUserSettings: async (email: string): Promise<UserSettings | null> => {
    try {
      const response = await api.get(
        `/api/user-settings/${encodeURIComponent(email)}`,
      );
      return response.data.data || null;
    } catch (error) {
      console.error('Error fetching user settings:', error);
      const axiosErr = error as AxiosError;
      if (axiosErr.response?.status === 404) {
        return null;
      }
      throw error;
    }
  },

  // Update user settings
  updateUserSettings: async (
    email: string,
    payload: Partial<UserSettings>,
  ): Promise<UserSettings> => {
    try {
      const response = await api.put(
        `/api/user-settings/${encodeURIComponent(email)}`,
        payload,
      );
      return response.data.data;
    } catch (error) {
      console.error('Error updating user settings:', error);
      throw error;
    }
  },
};

// Partner API (uses cookie-based admin auth, not Bearer token)
const partnerApi = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: { 'Content-Type': 'application/json' },
  withCredentials: true,
});

export interface PartnerOrganisation {
  id: string;
  name: string;
  billingEmail: string;
  planId: string;
  planStatus: string;
  billingMethod: string;
  currentProjects: number;
  maxProjects: number;
  monthlyUsage: {
    users: number;
    storageGB: number;
    bandwidthGB: number;
    retentionDays: number;
  };
}

export interface PartnerDashboardStats {
  totalOrganisations: number;
  activeOrganisations: number;
  totalProjects: number;
  totalUsers: number;
  totalStorageGB: number;
  totalBandwidthGB: number;
}

export const partnerApiService = {
  login: async (email: string, password: string) => {
    const response = await partnerApi.post('/api/admin/auth/login', {
      email,
      password,
    });
    return response.data;
  },

  verifyMfa: async (mfaToken: string, totpCode: string) => {
    const response = await partnerApi.post('/api/admin/auth/verify-mfa', {
      mfaToken,
      totpCode,
    });
    return response.data;
  },

  setupMfa: async () => {
    const response = await partnerApi.post('/api/admin/auth/mfa/setup');
    return response.data;
  },

  confirmMfa: async (totpCode: string) => {
    const response = await partnerApi.post('/api/admin/auth/mfa/confirm', {
      totpCode,
    });
    return response.data;
  },

  logout: async () => {
    const response = await partnerApi.post('/api/admin/auth/logout');
    return response.data;
  },

  getProfile: async () => {
    const response = await partnerApi.get('/api/partner/me');
    return response.data;
  },

  getDashboard: async (): Promise<PartnerDashboardStats> => {
    const response = await partnerApi.get('/api/partner/dashboard');
    return response.data.data;
  },

  getOrganisations: async (): Promise<PartnerOrganisation[]> => {
    const response = await partnerApi.get('/api/partner/organisations');
    return response.data.data || [];
  },

  createOrganisation: async (data: {
    name: string;
    billingEmail: string;
    planId: string;
    tierLevel?: number;
    billingMethod?: string;
    contactName?: string;
    contactEmail?: string;
  }): Promise<PartnerOrganisation> => {
    const response = await partnerApi.post('/api/partner/organisations', data);
    return response.data.data;
  },

  getOrganisation: async (id: string): Promise<PartnerOrganisation> => {
    const response = await partnerApi.get(`/api/partner/organisations/${id}`);
    return response.data.data;
  },

  updateOrganisation: async (
    id: string,
    data: {
      name?: string;
      billingEmail?: string;
      contactName?: string;
      contactEmail?: string;
    },
  ): Promise<PartnerOrganisation> => {
    const response = await partnerApi.put(
      `/api/partner/organisations/${id}`,
      data,
    );
    return response.data.data;
  },

  getLimits: async () => {
    const response = await partnerApi.get('/api/partner/limits');
    return response.data.data;
  },
};

export default apiService;
